import { Icons } from '@cockpitai/components/atoms'
import { Typography } from '@cockpitai/components/atoms/Typography'
import { routesPath } from '@cockpitai/constants/routesPath'
import { useDeleteFavorite } from '@cockpitai/integration/favorites/hooks'
import {
  NavigationItem,
  useLevelNavigation,
} from '@cockpitai/store/navigation/useLevelNavigation'
import { useSideBarStore } from '@cockpitai/store/navigation/useSideBarStore'
import { goToScrollTop } from '@cockpitai/utils'
import { sendAnalyticsEvent } from '@cockpitai/utils/analytics'
import { hideWord } from '@cockpitai/utils/hideWord'
import { urlCipher } from '@cockpitai/utils/urlCipher'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'

export type MenuItem = {
  to?: string
  onClick?: () => void
} & Partial<NavigationItem>

type MenuItemProps = {
  item: MenuItem
  onClick?: () => void
  resetChat?: () => void
  isFav?: boolean
  notStartBreadcrumb?: boolean
}

export function MenuItem({
  item,
  onClick,
  resetChat,
  isFav = false,
  notStartBreadcrumb = false,
}: MenuItemProps) {
  let isRemoved: boolean = false

  const { setInTransition, resetNavigation, addNavigation } =
    useLevelNavigation()

  const { mutateAsync } = useDeleteFavorite()

  const iconLetter = item?.name
    ? String(item.name).charAt(0).toUpperCase()
    : '-'

  const { removeFavorite } = useSideBarStore()

  const { pathname, search } = useLocation()

  const navigate = useNavigate()

  const content = (
    <>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-[1px] border-gray-600 text-xs font-bold">
        {iconLetter}
      </span>
      <Typography weight="light">{hideWord('REDE ', item.name)}</Typography>
    </>
  )

  const handleClick = () => {
    setInTransition(true)

    setTimeout(() => {
      setInTransition(false)
    }, 1000)

    item && goToScrollTop()

    resetNavigation()

    resetChat && resetChat()

    if (!notStartBreadcrumb && !isRemoved) {
      addNavigation({
        data: item.to ? urlCipher(item.to) : '',
        name: item.name ?? '',
        id: item.id,
      })
    }

    onClick && onClick()
  }

  const fullPath = search !== '' ? pathname + search : pathname

  const to = item?.to

  return (
    <>
      {to ? (
        <NavLink
          to={isFav ? routesPath.dashboard.home + to : to}
          onClick={handleClick}
          title={item.name}
          className={twMerge(
            isFav && 'fav-item',
            to === fullPath && 'item-active',
          )}>
          {content}
          {isFav && (
            <button
              onClick={(e) => {
                e.preventDefault()

                isRemoved = true

                item?.to && removeFavorite(urlCipher(item.to))

                resetNavigation()

                item?.id && mutateAsync(item.id)

                navigate(routesPath.dashboard.home, { replace: true })

                sendAnalyticsEvent('sidebar_fav_remove', {item_name: item.name})
              }}>
              <Icons.Trash className="text-white opacity-65 transition-all duration-150 hover:text-primary-600 hover:opacity-100" />
            </button>
          )}
        </NavLink>
      ) : (
        <button
          className={twMerge(to === fullPath && 'item-active')}
          onClick={handleClick}>
          {content}
        </button>
      )}
    </>
  )
}
